import { ComponentType, LazyExoticComponent, lazy } from 'react';

const CHUNK_RELOAD_FLAG = 'fc-system-chunk-reload';
const RETRY_DELAYS_MS = [1000, 3000];

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const loadWithRetry = async <M>(load: () => Promise<M>): Promise<M> => {
  for (const delay of RETRY_DELAYS_MS) {
    try {
      return await load();
    } catch {
      await wait(delay);
    }
  }
  return load();
};

const loadOrReload = async <M>(load: () => Promise<M>): Promise<M> => {
  try {
    const module = await loadWithRetry(load);
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    return module;
  } catch (error) {
    if (sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      throw error;
    }
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
    window.location.reload();
    return new Promise<never>(() => {});
  }
};

export function lazyPage<M extends { default: ComponentType<any> }>(
  load: () => Promise<M>,
): LazyExoticComponent<M['default']>;
export function lazyPage<K extends string, M extends Record<K, ComponentType<any>>>(
  load: () => Promise<M>,
  exportName: K,
): LazyExoticComponent<M[K]>;
export function lazyPage(
  load: () => Promise<Record<string, ComponentType<any>>>,
  exportName = 'default',
) {
  return lazy(async () => ({ default: (await loadOrReload(load))[exportName] }));
}
